import { useState } from 'react';
import { PlusIcon, ScaleIcon } from '@heroicons/react/24/outline';
import {
  useCreateCriterion,
  useCriteria,
  useDeleteCriterion,
  useUpdateCriterion,
  type CriteriaScope,
} from '@/hooks/useEndpointTestEvals';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { CriterionForm, CriterionRow } from './CriteriaEditor';
import { SuggestCriteriaButton } from './SuggestCriteriaButton';
import { TestingGuideLearnMore } from './TestingGuideLink';

/** Said once at the top of the section, above the list or the empty state (task 1186). */
export const CRITERIA_FRAMING =
  'Criteria are the rules every answer from this service is graded against. Each test grades every result against them; a test passes when every required criterion passes.';

/**
 * The Testing tab's Criteria section (task 1185 stub, reworked in task 1186).
 *
 * The service criteria the earlier section edited (task 1180), reframed so a
 * service owner is not asked to invent grading rules on a blank form:
 *
 * 1. A framing sentence says what criteria are and how a run passes.
 * 2. Empty: **Suggest criteria** hands the request to the Assistant (its
 *    Criteria Composer reads the service and saves suggestions), with **Add by
 *    hand** beside it.
 * 3. Populated: rows (name, one-line definition, required chip, "suggested" on
 *    generated criteria), Suggest criteria demoted to the header beside
 *    **Add criterion**.
 * 4. The row form stays collapsed until Add criterion, Add by hand or a row's
 *    Edit, and collapses again on save or cancel.
 *
 * The flag-off single-scroll layout keeps `ServiceCriteriaSection`; the
 * per-test criteria (Add / Edit test, result drawer) keep `CriteriaEditor`.
 */
export function CriteriaSection({ endpointId }: { endpointId: string }) {
  // The criteria feature set's lever (task 1215): off, the section is gone and
  // fetches nothing; the Testing tab drops its rail entry too.
  const criteriaEnabled = useFlag('innovate.fe.testing.criteria');
  return criteriaEnabled ? <CriteriaSectionContent endpointId={endpointId} /> : null;
}

function CriteriaSectionContent({ endpointId }: { endpointId: string }) {
  const scope: CriteriaScope = { kind: 'service', endpointId };
  const { data: criteria = [], isLoading, isError } = useCriteria(scope);
  const createCriterion = useCreateCriterion(scope);
  const updateCriterion = useUpdateCriterion(scope);
  const deleteCriterion = useDeleteCriterion(scope);
  const toast = useToast();
  // null = the form is collapsed; 'new' = adding; otherwise the id being edited.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);

  const run = async (action: () => Promise<unknown>, failure: string) => {
    try {
      await action();
      setEditingId(null);
    } catch (err) {
      toast.error(failure, err instanceof Error ? err.message : undefined);
    }
  };

  const newForm = (
    <CriterionForm
      saving={createCriterion.isPending}
      onCancel={() => setEditingId(null)}
      onSave={(data) => run(() => createCriterion.mutateAsync(data), 'Failed to add criterion')}
    />
  );

  return (
    <section
      data-testid="service-criteria"
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <ScaleIcon className="h-5 w-5 mr-2 text-gray-400" />
          Criteria{criteria.length > 0 && <span className="ml-1.5 text-gray-400">· {criteria.length}</span>}
        </h3>
        {criteria.length > 0 && (
          <div className="flex items-center gap-1.5">
            <SuggestCriteriaButton placement="header" />
            <button
              type="button"
              onClick={() => setEditingId('new')}
              data-testid="criteria-add-open"
              className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add criterion
            </button>
          </div>
        )}
      </div>

      <div className="px-4 py-3 space-y-3" data-testid="criteria-editor">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {CRITERIA_FRAMING}{' '}
          <TestingGuideLearnMore anchor="criteria" testId="criteria-learn-more" />
        </p>

        {isLoading ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading criteria…</p>
        ) : isError ? (
          <p className="text-xs text-red-500 dark:text-red-400">Couldn’t load criteria.</p>
        ) : criteria.length === 0 ? (
          editingId === 'new' ? (
            newForm
          ) : (
            <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center" data-testid="criteria-empty">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No criteria yet. Let the Assistant read this service and propose the rules it should be
                held to, or write one yourself.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <SuggestCriteriaButton placement="empty" />
                <button
                  type="button"
                  onClick={() => setEditingId('new')}
                  className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Add by hand
                </button>
              </div>
            </div>
          )
        ) : (
          <>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {criteria.map((criterion) =>
                editingId === criterion.id ? (
                  <li key={criterion.id} className="py-1.5">
                    <CriterionForm
                      initial={criterion}
                      kindLocked
                      saving={updateCriterion.isPending}
                      onCancel={() => setEditingId(null)}
                      onSave={({ name, definition, required }) =>
                        // PATCH cannot change `kind` (shipped contract), so it is not sent.
                        run(
                          () => updateCriterion.mutateAsync({ id: criterion.id, data: { name, definition, required } }),
                          'Failed to save criterion',
                        )
                      }
                    />
                  </li>
                ) : (
                  <CriterionRow
                    key={criterion.id}
                    testId={`criterion-${criterion.id}`}
                    criterion={criterion}
                    onEdit={() => setEditingId(criterion.id)}
                    onRemove={() => void run(() => deleteCriterion.mutateAsync(criterion.id), 'Failed to remove criterion')}
                  />
                ),
              )}
            </ul>
            {editingId === 'new' && newForm}
          </>
        )}
      </div>
    </section>
  );
}

export default CriteriaSection;
